import { defineStore } from 'pinia';
import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { dispatchNotification } from '~/lib/notificationDispatch';
import { canSendMessageDraft } from '~/lib/messageAttachments';
import { toMillis } from '~/lib/timestamps';
import { buildClientPortalPath } from '~/lib/tenantLinks';
import type { Message, MessageAttachment } from '~/types/models';

export type ThreadSummary = {
    threadId: string;
    clientId: string;
    clientName: string;
    lastMessage: string;
    lastDirection: Message['direction'];
    lastChannel?: Message['channel'];
    lastAt: unknown;
    count: number;
    channelCounts: {
        app: number;
        email: number;
        whatsapp: number;
    };
};

type WorkspaceChannelCounts = {
    app: number;
    email: number;
    whatsapp: number;
};

export const useMessagesStore = defineStore('messages', {
    state: () => ({
        messagesByThread: {} as Record<string, Message[]>,
        threadSummaries: [] as ThreadSummary[],
        workspaceChannelCounts: {
            app: 0,
            email: 0,
            whatsapp: 0,
        } as WorkspaceChannelCounts,
        loadingByThread: {} as Record<string, boolean>,
        loadingThreads: false,
        messagesError: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        _threadId(clientId: string): string {
            return `client:${clientId}`;
        },

        _sortMessages(messages: Message[]): Message[] {
            return [...messages].sort((a, b) => toMillis(a.createdAt) - toMillis(b.createdAt));
        },

        _recordMessage(created: Message): void {
            const existing = this.messagesByThread[created.threadId] || [];
            this.messagesByThread[created.threadId] = this._sortMessages([...existing, created]);

            const summaryIndex = this.threadSummaries.findIndex((summary) => summary.threadId === created.threadId);
            const attachmentCount = created.attachments?.length || 0;
            const lastMessage = created.content || `${attachmentCount} attachment${attachmentCount === 1 ? '' : 's'}`;
            if (summaryIndex > -1) {
                const existingSummary = this.threadSummaries[summaryIndex];
                if (!existingSummary) return;
                this.threadSummaries[summaryIndex] = {
                    ...existingSummary,
                    clientName: created.clientName || existingSummary.clientName,
                    lastMessage,
                    lastDirection: created.direction,
                    lastChannel: created.channel || 'app',
                    lastAt: created.createdAt,
                    count: existingSummary.count + 1,
                    channelCounts: {
                        app: Number(existingSummary.channelCounts?.app || 0) + 1,
                        email: Number(existingSummary.channelCounts?.email || 0),
                        whatsapp: Number(existingSummary.channelCounts?.whatsapp || 0),
                    },
                };
            } else {
                this.threadSummaries.push({
                    threadId: created.threadId,
                    clientId: created.clientId,
                    clientName: created.clientName || 'Unknown client',
                    lastMessage,
                    lastDirection: created.direction,
                    lastChannel: created.channel || 'app',
                    lastAt: created.createdAt,
                    count: 1,
                    channelCounts: { app: 1, email: 0, whatsapp: 0 },
                });
            }

            this.threadSummaries = [...this.threadSummaries].sort(
                (a, b) => toMillis(b.lastAt) - toMillis(a.lastAt),
            );
        },

        _replaceMessage(updated: Message): void {
            const existing = this.messagesByThread[updated.threadId] || [];
            this.messagesByThread[updated.threadId] = this._sortMessages(
                existing.map(message => message.id === updated.id ? updated : message),
            );
        },

        _deletedMessagePatch(deletedAt: Date) {
            return {
                content: '',
                subject: '',
                assistantContext: '',
                attachments: [],
                actionPath: '',
                actionLabel: '',
                responseRequired: false,
                responseStatus: 'resolved' as const,
                responseResolvedAt: deletedAt,
                responseReminderStage: 0,
                deletedAt,
                updatedAt: deletedAt,
            };
        },

        async deleteMessage(message: Message): Promise<void> {
            if (!message.id || message.entityType === 'form_submission' || message.entityType === 'portal_form_request') {
                throw new Error('This inbox activity cannot be deleted as a message.');
            }

            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const deletedAt = new Date();
            await updateDoc(doc($db, `tenants/${tenantId}/messages/${message.id}`), this._deletedMessagePatch(deletedAt));

            const messages = this.messagesByThread[message.threadId] || [];
            this.messagesByThread[message.threadId] = messages.filter(entry => entry.id !== message.id);
            await this.fetchThreadSummaries();
        },

        async deleteThread(clientId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const threadId = this._threadId(clientId);
            const deletedAt = new Date();
            const threadQuery = query(
                collection($db, `tenants/${tenantId}/messages`),
                where('threadId', '==', threadId),
            );
            const snapshot = await getDocs(threadQuery);

            const activeDocs = snapshot.docs.filter(messageDoc => !messageDoc.data().deletedAt);
            for (let offset = 0; offset < activeDocs.length; offset += 450) {
                const batch = writeBatch($db);
                for (const messageDoc of activeDocs.slice(offset, offset + 450)) {
                    batch.update(messageDoc.ref, this._deletedMessagePatch(deletedAt));
                }
                await batch.commit();
            }

            await updateDoc(doc($db, `tenants/${tenantId}/clients/${clientId}`), {
                inboxThreadDeletedAt: deletedAt,
            });

            const clientStore = useClientStore();
            const clientIndex = clientStore.clients.findIndex(client => client.id === clientId);
            if (clientIndex >= 0) {
                clientStore.clients[clientIndex] = {
                    ...clientStore.clients[clientIndex]!,
                    inboxThreadDeletedAt: deletedAt,
                };
            }
            if (clientStore.currentClient?.id === clientId) {
                clientStore.currentClient = {
                    ...clientStore.currentClient,
                    inboxThreadDeletedAt: deletedAt,
                };
            }

            this.messagesByThread[threadId] = [];
            await this.fetchThreadSummaries();
        },

        async updateResponseFollowUp(
            messageId: string,
            action: 'mark_waiting' | 'send_reminder' | 'resolve',
        ): Promise<Message> {
            const tenantId = this._getTenantId();
            const { $auth } = useNuxtApp();
            const authUser = $auth.currentUser;
            if (!authUser) throw new Error('You must be logged in to update response follow-ups.');

            const response = await $fetch<{ message: Message; reminder?: Message }>('/api/messages/response-follow-up', {
                method: 'POST',
                headers: { authorization: `Bearer ${await authUser.getIdToken()}` },
                body: { tenantId, messageId, action },
            });

            this._replaceMessage(response.message);
            if (response.reminder) this._recordMessage(response.reminder);
            return response.message;
        },

        async fetchThread(clientId: string): Promise<Message[]> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const threadId = this._threadId(clientId);
            this.loadingByThread[threadId] = true;
            this.messagesError = null;

            try {
                const collectionRef = collection($db, `tenants/${tenantId}/messages`);
                const [snap, clientSnap] = await Promise.all([
                    getDocs(collectionRef),
                    getDoc(doc($db, `tenants/${tenantId}/clients/${clientId}`)),
                ]);
                const deletedAt = toMillis(clientSnap.data()?.inboxThreadDeletedAt);
                const allMessages = snap.docs.map((messageDoc) => ({
                    id: messageDoc.id,
                    ...messageDoc.data(),
                } as Message));

                const threadMessages = allMessages.filter((message) => (
                    message.threadId === threadId
                    && !message.deletedAt
                    && (!deletedAt || toMillis(message.createdAt) > deletedAt)
                ));
                this.messagesByThread[threadId] = this._sortMessages(threadMessages);
                return this.messagesByThread[threadId] || [];
            } catch (error: any) {
                this.messagesError = error.message || 'Failed to load messages.';
                console.error('Error fetching message thread:', error);
                return [];
            } finally {
                this.loadingByThread[threadId] = false;
            }
        },

        async fetchThreadSummaries(): Promise<ThreadSummary[]> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loadingThreads = true;
            this.messagesError = null;

            try {
                const collectionRef = collection($db, `tenants/${tenantId}/messages`);
                const [snap, clientSnap] = await Promise.all([
                    getDocs(collectionRef),
                    getDocs(collection($db, `tenants/${tenantId}/clients`)),
                ]);
                const deletedAtByClientId = new Map(clientSnap.docs.map(clientDoc => [
                    clientDoc.id,
                    toMillis(clientDoc.data().inboxThreadDeletedAt),
                ]));
                const allMessages = snap.docs.map((messageDoc) => ({
                    id: messageDoc.id,
                    ...messageDoc.data(),
                } as Message));

                const summaryMap: Record<string, ThreadSummary> = {};
                const channelCounts: WorkspaceChannelCounts = {
                    app: 0,
                    email: 0,
                    whatsapp: 0,
                };
                for (const message of allMessages) {
                    const threadDeletedAt = deletedAtByClientId.get(message.clientId) || 0;
                    if (message.deletedAt || (threadDeletedAt && toMillis(message.createdAt) <= threadDeletedAt)) continue;
                    const existing = summaryMap[message.threadId];
                    const messageMillis = toMillis(message.createdAt);
                    const existingMillis = existing ? toMillis(existing.lastAt) : 0;
                    const channel = message.channel || 'app';
                    if (channel === 'email') channelCounts.email += 1;
                    else if (channel === 'whatsapp') channelCounts.whatsapp += 1;
                    else channelCounts.app += 1;

                    if (!existing || messageMillis >= existingMillis) {
                        summaryMap[message.threadId] = {
                            threadId: message.threadId,
                            clientId: message.clientId,
                            clientName: message.clientName || 'Unknown client',
                            lastMessage: message.content || (message.attachments?.length
                                ? `${message.attachments.length} attachment${message.attachments.length === 1 ? '' : 's'}`
                                : ''),
                            lastDirection: message.direction,
                            lastChannel: channel,
                            lastAt: message.createdAt,
                            count: existing ? existing.count + 1 : 1,
                            channelCounts: {
                                app: existing?.channelCounts?.app || 0,
                                email: existing?.channelCounts?.email || 0,
                                whatsapp: existing?.channelCounts?.whatsapp || 0,
                            },
                        };
                    } else if (existing) {
                        existing.count += 1;
                    }

                    const target = summaryMap[message.threadId];
                    if (target) {
                        if (channel === 'email') target.channelCounts.email += 1;
                        else if (channel === 'whatsapp') target.channelCounts.whatsapp += 1;
                        else target.channelCounts.app += 1;
                    }
                }

                this.threadSummaries = Object.values(summaryMap).sort(
                    (a, b) => toMillis(b.lastAt) - toMillis(a.lastAt),
                );
                this.workspaceChannelCounts = channelCounts;

                return this.threadSummaries;
            } catch (error: any) {
                this.messagesError = error.message || 'Failed to load message threads.';
                console.error('Error fetching message threads:', error);
                return [];
            } finally {
                this.loadingThreads = false;
            }
        },

        async sendMessage(params: {
            clientId: string;
            clientName?: string;
            content: string;
            direction?: Message['direction'];
            attachments?: MessageAttachment[];
        }): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.messagesError = null;

            const trimmed = params.content.trim();
            const attachments = (params.attachments || []).slice(0, 5);
            if (!canSendMessageDraft(trimmed, attachments.length)) {
                throw new Error('Add a message or attachment before sending.');
            }

            const authStore = useAuthStore();
            const userStore = useUserStore();
            const senderId = authStore.user?.uid || userStore.user?.id;
            if (!senderId) {
                throw new Error('You must be logged in to send messages.');
            }

            const senderName = authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || 'Unknown';
            const senderAvatar = authStore.user?.photoURL || userStore.user?.avatarURL || '';
            const direction = params.direction || 'tenant_to_client';
            const threadId = this._threadId(params.clientId);
            const clientStore = useClientStore();
            const resolvedClient = clientStore.clients.find((entry) => entry.id === params.clientId) || await clientStore.fetchClientById(params.clientId);

            const payload = {
                tenantId,
                threadId,
                clientId: params.clientId,
                clientName: params.clientName || 'Unknown client',
                content: trimmed,
                attachments,
                direction,
                senderId,
                senderName,
                senderAvatar,
                createdAt: new Date(),
                updatedAt: new Date(),
            };

            const messageRef = await addDoc(collection($db, `tenants/${tenantId}/messages`), payload);

            const created = { id: messageRef.id, ...payload } as Message;
            this._recordMessage(created);

            try {
                await updateDoc(doc($db, `tenants/${tenantId}/clients/${params.clientId}`), {
                    'lifecycle.lastEngagedAt': created.createdAt,
                    updatedAt: created.updatedAt,
                });
            } catch (clientActivityError) {
                console.warn('Failed to update client last engagement timestamp after message send:', clientActivityError);
            }

            try {
                const clientSlug = String(resolvedClient?.slug || '').trim();

                if (clientSlug) {
                    await dispatchNotification({
                        tenantId,
                        surface: 'client',
                        eventType: 'team_message_received',
                        title: 'New message from your team',
                        body: trimmed || `${attachments.length} attachment${attachments.length === 1 ? '' : 's'} shared`,
                        recipients: [{
                            type: 'portal_access',
                            clientId: params.clientId,
                            permission: 'viewMessages',
                        }],
                        entityType: 'message',
                        entityId: messageRef.id,
                        clientId: params.clientId,
                        actionPath: buildClientPortalPath({
                            tenantId,
                            clientSlug,
                            section: 'messages',
                        }),
                        actionLabel: 'Open messages',
                        actorId: senderId,
                        actorName: senderName,
                        actorAvatarURL: senderAvatar,
                    });
                }
            } catch (notificationError) {
                console.warn('Failed to dispatch client message notification:', notificationError);
            }

            return messageRef.id;
        },

        async sendAutomatedMessage(params: {
            clientId: string;
            clientName?: string;
            content: string;
            subject: string;
            actionPath: string;
            actionLabel: string;
            entityType: 'approval' | 'task';
            entityId: string;
        }): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const authStore = useAuthStore();
            const userStore = useUserStore();
            const tenantStore = useTenantStore();
            const senderId = authStore.user?.uid || userStore.user?.id;
            if (!senderId) throw new Error('You must be logged in to send automated messages.');

            const now = new Date();
            const payload: Omit<Message, 'id'> = {
                tenantId,
                threadId: this._threadId(params.clientId),
                clientId: params.clientId,
                clientName: params.clientName || 'Client',
                content: params.content.trim(),
                channel: 'app',
                direction: 'tenant_to_client',
                senderId,
                senderName: tenantStore.currentTenant?.name || 'Your team',
                senderAvatar: tenantStore.currentTenant?.tenantImageURL || '',
                subject: params.subject,
                automated: true,
                actionPath: params.actionPath,
                actionLabel: params.actionLabel,
                entityType: params.entityType,
                entityId: params.entityId,
                attachments: [],
                createdAt: now,
                updatedAt: now,
            };

            const messageRef = await addDoc(collection($db, `tenants/${tenantId}/messages`), payload);
            this._recordMessage({ id: messageRef.id, ...payload });
            return messageRef.id;
        },
    },
});
